import React, { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Copy, Lock, RotateCcw, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import {
  Breadcrumbs, Container, Eyebrow, H1, H2, H3, Lede, Section, EmptyState, Spinner,
} from "../components/Primitives";
import { GuaranteeLine } from "../components/GuaranteeLine";
import { createOrder, deleteData, exportData, getOrder, getOrderBumps, cartCheckpoint, apiError } from "../lib/api";
import { useCart } from "../lib/cart";
import { useCurrency } from "../lib/currency";
import { useSEO } from "../lib/seo";

const COUNTRIES = [
  ["IN", "India"], ["US", "United States"], ["GB", "United Kingdom"], ["AE", "United Arab Emirates"],
  ["CA", "Canada"], ["AU", "Australia"], ["DE", "Germany (EU)"], ["SG", "Singapore"],
];

export function Cart() {
  const cart = useCart();
  const { format } = useCurrency();

  useSEO({ title: "Your basket", noindex: true });

  return (
    <Section>
      <Container>
        <Eyebrow>Nothing is added between here and payment</Eyebrow>
        <H1 className="mt-4">Your basket</H1>

        {!cart.items.length ? (
          <div className="mt-10 max-w-xl">
            <EmptyState title="Nothing in here yet">
              <Link to="/shop/gemstones" className="link-u">Browse certified stones →</Link> or{" "}
              <Link to="/consult/suitability-session" className="link-u">book the ₹499 session</Link>, which can
              also tell you that you need nothing.
            </EmptyState>
          </div>
        ) : (
          <div className="mt-10 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
            <ul className="divide-y divide-rule" data-testid="cart-items">
              {cart.items.map((i) => (
                <li key={i.slug} className="flex gap-4 py-5" data-testid={`cart-item-${i.slug}`}>
                  <img src={i.image} alt="" className="h-24 w-24 shrink-0 border border-rule object-cover" />
                  <div className="flex-1">
                    <Link to={`/product/${i.slug}`} className="ui text-sm font-medium text-ink hover:text-gold">
                      {i.title}
                    </Link>
                    <p className="ui text-xs text-dim">{i.subtitle}</p>
                    <p className="num mt-1 text-xs text-dim">SKU {i.sku}</p>
                    {i.setting && (
                      <p className="ui mt-1 text-xs text-dim">
                        Setting: {i.setting}{i.ring_size ? ` · size ${i.ring_size}` : ""} (+{format(i.setting_price_inr)})
                      </p>
                    )}
                    <p className="ui mt-1 text-xs text-verify">
                      {i.energisation ? "Energisation included, no charge" : "Energisation declined — same price"}
                    </p>
                    {(i.addons || []).length > 0 && (
                      <ul className="ui mt-3 space-y-1 border-l border-rule pl-3" data-testid={`cart-addons-${i.slug}`}>
                        {i.addons.map((a) => (
                          <li key={a.id} className="flex items-center justify-between gap-3 text-xs text-dim">
                            <span>+ {a.label}</span>
                            <span className="flex items-center gap-2">
                              <span className="num text-ink">{format(a.price_inr)}</span>
                              <button
                                type="button"
                                onClick={() => cart.removeAddon(i.slug, a.id)}
                                className="text-dim hover:text-alert"
                                aria-label={`Remove ${a.label}`}
                                data-testid={`cart-addon-remove-${i.slug}-${a.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="num text-sm text-deep">
                      {format(i.price_inr + (i.setting_price_inr || 0) + (i.addons || []).reduce((s, a) => s + a.price_inr, 0))}
                    </p>
                    <button
                      type="button"
                      onClick={() => cart.remove(i.slug)}
                      data-testid={`cart-remove-${i.slug}`}
                      aria-label={`Remove ${i.title}`}
                      className="mt-3 text-dim transition-colors hover:text-alert"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="card-s h-fit p-6">
              <H3>Summary</H3>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="ui text-dim">Goods, inclusive of tax</dt>
                  <dd className="num text-ink">{format(cart.goodsTotal)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="ui text-dim">Insured shipping</dt>
                  <dd className="num text-verify">Included</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="ui text-dim">Certification &amp; energisation</dt>
                  <dd className="num text-verify">Included</dd>
                </div>
              </dl>
              <p className="ui mt-4 text-xs text-dim">
                Shipping and any duties are calculated at checkout before you pay.
              </p>
              <p className="ui mt-2 text-xs text-dim">
                Duties and destination taxes for international orders are calculated and shown on the next
                screen, before payment. Nothing appears after it.
              </p>
              <Link to="/checkout" className="btn btn-primary mt-6 w-full" data-testid="go-to-checkout">
                Continue to checkout
              </Link>
            </div>
          </div>
        )}
      </Container>
    </Section>
  );
}

export function Checkout() {
  const cart = useCart();
  const { format } = useCurrency();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address_line1: "", address_line2: "",
    city: "", state: "", postcode: "", country: "IN", consultation_credit_ref: "",
  });
  const [marketing, setMarketing] = useState(false);
  const [bumpIds, setBumpIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  const bumpsQ = useQuery({ queryKey: ["order-bumps"], queryFn: getOrderBumps });
  const bumps = bumpsQ.data?.items || [];
  const selectedBumps = bumps.filter((b) => bumpIds.includes(b.id));
  const bumpTotal = selectedBumps.reduce((s, b) => s + b.price_inr, 0);

  useSEO({ title: "Checkout", noindex: true });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  /* Abandonment checkpoint: once we know the email, snapshot the basket so the
     (MOCKED, logged-not-sent) +4h / +48h sequence has something honest to say. */
  const checkpoint = () => {
    if (!form.email || !form.email.includes("@") || !cart.items.length) return;
    cartCheckpoint({
      email: form.email,
      items: cart.items.map((i) => ({ slug: i.slug, title: i.title, price_inr: i.price_inr, image: i.image })),
    }).catch(() => {});
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await createOrder({
        items: cart.items.map((i) => ({
          slug: i.slug, qty: i.qty, setting: i.setting, ring_size: i.ring_size,
          energisation: i.energisation,
          addons: (i.addons || []).map((a) => a.id),
        })),
        ...form,
        consultation_credit_ref: form.consultation_credit_ref || null,
        currency: "INR",
        marketing_consent: marketing,
        bumps: bumpIds,
      });
      setOrder(r.order);
      cart.clear();
      toast.success("Order placed.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (order) {
    return <OrderSummary order={order} justPlaced />;
  }

  if (!cart.items.length) {
    return (
      <Section>
        <Container className="max-w-xl">
          <EmptyState title="Your basket is empty">
            <Link to="/shop" className="link-u">Back to the shop →</Link>
          </EmptyState>
        </Container>
      </Section>
    );
  }

  return (
    <Section>
      <Container className="grid gap-12 lg:grid-cols-[1.2fr_1fr]">
        <form onSubmit={submit} className="space-y-4" data-testid="checkout-form">
          <Eyebrow>No forced account creation</Eyebrow>
          <H1>Checkout</H1>
          <p className="ui text-sm text-dim">
            Guest checkout. You can track the order at /track with the order number, without a login.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="field-label">Full name</span>
              <input required value={form.name} onChange={set("name")} className="field" data-testid="checkout-name" />
            </label>
            <label className="block">
              <span className="field-label">Email</span>
              <input required type="email" value={form.email} onChange={set("email")} onBlur={checkpoint} className="field" data-testid="checkout-email" />
            </label>
            <label className="block">
              <span className="field-label">Phone</span>
              <input required value={form.phone} onChange={set("phone")} className="field num" data-testid="checkout-phone" />
            </label>
            <label className="block sm:col-span-2">
              <span className="field-label">Address line 1</span>
              <input required value={form.address_line1} onChange={set("address_line1")} className="field" data-testid="checkout-address1" />
            </label>
            <label className="block sm:col-span-2">
              <span className="field-label">Address line 2 (optional)</span>
              <input value={form.address_line2} onChange={set("address_line2")} className="field" />
            </label>
            <label className="block">
              <span className="field-label">City</span>
              <input required value={form.city} onChange={set("city")} className="field" data-testid="checkout-city" />
            </label>
            <label className="block">
              <span className="field-label">State / region</span>
              <input value={form.state} onChange={set("state")} className="field" />
            </label>
            <label className="block">
              <span className="field-label">Postcode / pincode</span>
              <input required value={form.postcode} onChange={set("postcode")} className="field num" data-testid="checkout-postcode" />
            </label>
            <label className="block">
              <span className="field-label">Country</span>
              <select value={form.country} onChange={set("country")} className="field ui" data-testid="checkout-country">
                {COUNTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="field-label">Consultation reference, to credit ₹499 (optional)</span>
              <input
                value={form.consultation_credit_ref}
                onChange={set("consultation_credit_ref")}
                placeholder="SME-C-XXXXXXXX"
                className="field num"
                data-testid="checkout-credit-ref"
              />
              <span className="ui mt-1 block text-xs text-dim">
                The Proof-First Session fee is credited against any purchase over ₹5,000 within 30 days.
              </span>
            </label>
          </div>

          <label htmlFor="checkout-marketing" className="flex cursor-pointer items-start gap-3 pt-2">
            <input
              id="checkout-marketing"
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              data-testid="checkout-marketing"
              className="mt-1 h-4 w-4 accent-[var(--c-gold)]"
            />
            <span className="ui text-xs text-dim">
              Optional: email me guidance and new arrivals. Unticked by default, and nothing else changes
              if you leave it that way.
            </span>
          </label>

          {bumps.map((b) => (
            <label
              key={b.id}
              htmlFor={`bump-${b.id}`}
              className="flex cursor-pointer items-start gap-3 border border-rule bg-surface p-4 transition-colors hover:border-gold"
              data-testid={`order-bump-${b.id}`}
            >
              <input
                id={`bump-${b.id}`}
                type="checkbox"
                checked={bumpIds.includes(b.id)}
                onChange={(e) => setBumpIds(e.target.checked ? [...bumpIds, b.id] : bumpIds.filter((x) => x !== b.id))}
                className="mt-1 h-4 w-4 accent-[var(--c-gold)]"
                data-testid={`order-bump-check-${b.id}`}
              />
              <span>
                <span className="ui block text-sm font-medium text-ink">
                  {b.label} — <span className="num">{format(b.price_inr)}</span>
                </span>
                <span className="ui mt-1 block text-xs text-dim">{b.description}</span>
              </span>
            </label>
          ))}

          <p className="ui text-xs text-saffron">
            DEMO CHECKOUT — no payment provider is connected in this build. Placing the order creates a
            real order record without taking money.
          </p>

          <button type="submit" disabled={busy} className="btn btn-primary" data-testid="place-order">
            {busy ? "Placing order…" : "Place order"}
          </button>
          <GuaranteeLine testId="checkout-guarantee">
            Full refund if the stone isn't what our report says. 30-day return either way.
          </GuaranteeLine>
          <div className="ui flex flex-wrap items-center gap-x-5 gap-y-2 pt-1 text-xs text-dim" data-testid="checkout-trust-row">
            <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-verify" aria-hidden="true" /> Secure payment</span>
            <span className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-verify" aria-hidden="true" /> Insured shipping</span>
            <span className="flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5 text-verify" aria-hidden="true" /> 30-day return</span>
          </div>
        </form>

        <div className="card-s h-fit p-6">
          <H3>Order summary</H3>
          <ul className="mt-4 divide-y divide-rule">
            {cart.items.map((i) => (
              <li key={i.slug} className="py-3">
                <div className="flex justify-between gap-4">
                  <div>
                    <p className="ui text-sm text-ink">{i.title}</p>
                    <p className="num text-xs text-dim">
                      {i.sku} · HSN {i.type === "gemstone" ? "7103" : i.setting ? "7113" : "nil"}
                    </p>
                  </div>
                  <p className="num shrink-0 text-sm text-ink">
                    {format(i.price_inr + (i.setting_price_inr || 0))}
                  </p>
                </div>
                {(i.addons || []).length > 0 && (
                  <ul className="ui mt-2 space-y-1 border-l border-rule pl-3">
                    {i.addons.map((a) => (
                      <li key={a.id} className="flex items-center justify-between gap-3 text-xs text-dim" data-testid={`summary-child-${a.id}`}>
                        <span>+ {a.label}</span>
                        <span className="num text-ink">{format(a.price_inr)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <dl className="mt-4 space-y-2 border-t border-rule pt-4 text-sm">
            <div className="flex justify-between">
              <dt className="ui text-dim">Goods (tax inclusive)</dt>
              <dd className="num text-ink">{format(cart.goodsTotal)}</dd>
            </div>
            {selectedBumps.map((b) => (
              <div className="flex justify-between" key={b.id} data-testid={`summary-bump-${b.id}`}>
                <dt className="ui text-dim">{b.label.replace("Add a ", "")}</dt>
                <dd className="num text-ink">{format(b.price_inr)}</dd>
              </div>
            ))}
            <div className="flex justify-between">
              <dt className="ui text-dim">Insured shipping</dt>
              <dd className="num text-verify">Free</dd>
            </div>
            <div className="flex justify-between">
              <dt className="ui text-dim">Duties &amp; destination tax</dt>
              <dd className="num text-dim">
                {form.country === "IN" ? "Not applicable" : "Calculated on placing the order (DDP)"}
              </dd>
            </div>
            <div className="flex justify-between border-t border-rule pt-2">
              <dt className="ui font-semibold text-ink">Total (tax inclusive)</dt>
              <dd className="num font-semibold text-gold" data-testid="checkout-total">
                {format(cart.goodsTotal + bumpTotal)}
              </dd>
            </div>
          </dl>
          <p className="ui mt-4 text-xs text-dim">
            Goods and services are always separate line items with separate HSN and SAC codes and separate
            tax treatment. They are never combined into a single-price bundle — that is a GST requirement,
            not a preference.
          </p>
        </div>
      </Container>
    </Section>
  );
}

function OrderSummary({ order, justPlaced }) {
  const { format } = useCurrency();
  const [copied, setCopied] = useState("");
  const firstProduct = (order.lines || []).find((l) => l.kind !== "add_on");
  const copy = (key, text) => {
    const done = () => {
      setCopied(key);
      toast.success("Link copied.");
    };
    const fallback = () => {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        done();
      } catch (e) {
        toast.error("Could not copy — long-press the link instead.");
      }
      document.body.removeChild(ta);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(fallback);
    } else {
      fallback();
    }
  };
  return (
    <Section>
      <Container className="max-w-3xl">
        <Eyebrow>{justPlaced ? "Order placed" : "Order status"}</Eyebrow>
        <H1 className="mt-4">
          Order <span className="num" data-testid="order-number">{order.order_number}</span>
        </H1>
        <p className="ui mt-3 text-sm text-dim">
          Dispatch is due by{" "}
          <span className="num">
            {new Date(order.dispatch_due_utc).toLocaleString([], { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
          </span>
          . If we miss it, your order is refunded in full automatically — you do not need to ask.
        </p>

        <div className="card-s mt-8 p-6">
          <H3>Lines</H3>
          <table className="mt-4 w-full text-sm">
            <thead>
              <tr className="border-b border-rule">
                <th scope="col" className="ui py-2 text-left text-xs uppercase tracking-wider text-dim">Item</th>
                <th scope="col" className="ui py-2 text-left text-xs uppercase tracking-wider text-dim">HSN / SAC</th>
                <th scope="col" className="ui py-2 text-right text-xs uppercase tracking-wider text-dim">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((l) => (
                <tr key={l.slug} className="border-b border-rule">
                  <td className="ui py-3 pr-4 text-ink">
                    {l.title}
                    <span className="num block text-xs text-dim">{l.sku}</span>
                    {l.setting && <span className="ui block text-xs text-dim">Setting: {l.setting} {l.ring_size}</span>}
                  </td>
                  <td className="num py-3 pr-4 text-dim">{l.hsn_code || "nil"} · GST {l.gst_rate}%</td>
                  <td className="num py-3 text-right text-ink">{format(l.line_total_inr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <dl className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="ui text-dim">Goods</dt><dd className="num">{format(order.goods_subtotal_inr)}</dd></div>
            <div className="flex justify-between"><dt className="ui text-dim">Duties &amp; destination tax (DDP)</dt><dd className="num">{format(order.duty_and_destination_tax_inr)}</dd></div>
            <div className="flex justify-between"><dt className="ui text-dim">Insured shipping</dt><dd className="num text-verify">Free</dd></div>
            {order.credit_applied_inr > 0 && (
              <div className="flex justify-between"><dt className="ui text-dim">Consultation credit applied</dt><dd className="num text-verify">−{format(order.credit_applied_inr)}</dd></div>
            )}
            <div className="flex justify-between border-t border-rule pt-2">
              <dt className="ui font-semibold text-ink">Total paid</dt>
              <dd className="num text-lg font-semibold text-gold">{format(order.total_inr)}</dd>
            </div>
          </dl>
        </div>

        <div className="card-s mt-6 p-6">
          <H3>Delivery</H3>
          <p className="ui mt-2 text-sm text-dim">
            {order.name} · {order.shipping.line1}
            {order.shipping.line2 ? `, ${order.shipping.line2}` : ""}, {order.shipping.city}{" "}
            <span className="num">{order.shipping.postcode}</span>, {order.country}
          </p>
          <p className="num mt-3 text-sm text-ink">
            Expected {new Date(order.delivery_date).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })} · {order.carrier} · insured
          </p>
          <p className="num mt-1 text-xs text-dim">Tracking: {order.tracking_number}</p>
          <p className="ui mt-3 text-xs text-dim">
            We also send a photograph of the sealed parcel with the waybill visible. Track any time at{" "}
            <Link to="/track" className="link-u">/track</Link> — no login required.
          </p>
        </div>

        {justPlaced && (
          <div className="card-s mt-6 p-6" data-testid="what-happens-next">
            <H3>What happens next</H3>
            <ol className="ui mt-3 list-decimal space-y-2 pl-5 text-sm leading-[1.7] text-dim">
              <li>
                Verify the laboratory report now — the certificate on{" "}
                {firstProduct ? (
                  <Link to={`/product/${firstProduct.slug}`} className="link-u">your item's page</Link>
                ) : "your item's page"}{" "}
                opens the laboratory's own website, not ours.
              </li>
              <li>Photographs of your exact piece, loose on a grey card with a millimetre scale, are sent before anything is set.</li>
              <li>Energisation is performed and its video follows with dispatch — included, never an upgrade.</li>
              <li>
                Track any time at{" "}
                <Link to={`/track?order=${order.order_number}`} className="link-u">/track</Link> — no login needed.
              </li>
            </ol>
          </div>
        )}

        {justPlaced && firstProduct && (
          <div className="card-s mt-6 p-6" data-testid="share-for-verification">
            <H3>Have your new stone checked by anyone</H3>
            <p className="ui mt-2 text-sm leading-[1.7] text-dim">
              Send the product page to your own astrologer or jeweller — the laboratory report on it
              verifies on the lab's own website, so they can check it without trusting us at all. We
              would rather be checked than believed.
            </p>
            <button
              type="button"
              onClick={() => copy("cert", `${window.location.origin}/product/${firstProduct.slug}`)}
              className="btn btn-secondary mt-4"
              data-testid="copy-cert-link"
            >
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              {copied === "cert" ? "Copied" : "Copy the certificate page link"}
            </button>
          </div>
        )}

        {justPlaced && order.referral_code && (
          <div className="card-s mt-6 border-gold/50 p-6" data-testid="referral-block">
            <p className="eyebrow">Know someone searching for a genuine stone?</p>
            <p className="ui mt-3 text-sm leading-[1.7] text-dim">
              Send them this page — when they make their first purchase, you both get ₹500 credit.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="num border border-rule bg-wash px-3 py-2 text-sm text-deep" data-testid="referral-code">
                {order.referral_code}
              </span>
              <button
                type="button"
                onClick={() => copy("ref", `${window.location.origin}/shop/gemstones?ref=${order.referral_code}`)}
                className="btn btn-secondary"
                data-testid="copy-referral-link"
              >
                <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                {copied === "ref" ? "Copied" : "Copy your referral link"}
              </button>
            </div>
            <p className="ui mt-3 text-xs text-dim">
              The credit activates automatically when live payments are switched on — your code is
              already saved against this order.
            </p>
          </div>
        )}

        <p className="ui mt-8 text-xs text-saffron">
          DEMO ORDER — no payment was taken and nothing will be shipped.
        </p>
      </Container>
    </Section>
  );
}

export function OrderPage() {
  const { id } = useParams();
  const { data, isLoading, isError } = useQuery({ queryKey: ["order", id], queryFn: () => getOrder(id) });
  useSEO({ title: `Order ${id}`, noindex: true });
  if (isLoading) return <Container className="py-20"><Spinner /></Container>;
  if (isError) {
    return (
      <Container className="py-20">
        <EmptyState title="No order with that number">
          Guest tracking needs the number from your confirmation email.
        </EmptyState>
      </Container>
    );
  }
  return <OrderSummary order={data.order} />;
}

export function Track() {
  const [params, setParams] = useSearchParams();
  const [num, setNum] = useState(params.get("order") || "");
  const q = params.get("order");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["order", q], queryFn: () => getOrder(q), enabled: Boolean(q),
  });

  useSEO({
    title: "Track your order — no login required",
    description: "Enter your order number to see dispatch status, delivery date and tracking. Guest tracking, no account needed.",
    canonical: "/track",
  });

  return (
    <Section>
      <Container className="max-w-2xl">
        <Eyebrow>No login required</Eyebrow>
        <H1 className="mt-4">Track your order</H1>
        <form
          onSubmit={(e) => { e.preventDefault(); setParams({ order: num }); }}
          className="mt-8 flex gap-2"
          data-testid="track-form"
        >
          <input
            required
            value={num}
            onChange={(e) => setNum(e.target.value)}
            placeholder="SME-XXXXXXXX"
            className="field num"
            data-testid="track-input"
          />
          <button type="submit" className="btn btn-primary shrink-0" data-testid="track-submit">Track</button>
        </form>
        {isLoading && <Spinner />}
        {isError && <p className="ui mt-6 text-sm text-alert">No order with that number.</p>}
      </Container>
      {data?.order && <OrderSummary order={data.order} />}
    </Section>
  );
}

export function AccountData() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Download or delete my data — DPDP rights",
    description: "One-click export and one-click deletion of everything we hold, with an emailed confirmation.",
    canonical: "/account/data",
  });

  const doExport = async () => {
    setBusy(true);
    try {
      const r = await exportData(email);
      setResult(r);
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `shreem-mantra-energy-data-${email}.json`;
      a.click();
      toast.success("Exported and downloaded.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await deleteData(email);
      setResult(r);
      toast.success(r.message);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section>
      <Container className="max-w-2xl">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Account", to: "/account" }, { label: "My data" }]} />
        <Eyebrow className="mt-6">DPDP Act 2023 rights</Eyebrow>
        <H1 className="mt-4">Download or delete my data</H1>
        <Lede className="mt-5">
          One click to export everything we hold. One click to purge it, with an emailed confirmation.
          Withdrawal is as easy as consent, which is the whole requirement.
        </Lede>

        <div className="card-s mt-8 p-6">
          <label className="block">
            <span className="field-label">The email address you used</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field"
              data-testid="account-data-email"
            />
          </label>
          <div className="mt-5 flex flex-wrap gap-3">
            <button type="button" disabled={!email || busy} onClick={doExport} className="btn btn-secondary" data-testid="export-data">
              Download everything
            </button>
            <button type="button" disabled={!email || busy} onClick={doDelete} className="btn btn-primary" data-testid="delete-data">
              Delete everything
            </button>
          </div>
          {result && (
            <pre className="num mt-6 max-h-72 overflow-auto border border-rule bg-wash p-4 text-xs" data-testid="account-data-result">
              {JSON.stringify(result, null, 2)}
            </pre>
          )}
        </div>

        <p className="ui mt-6 text-xs text-dim">
          Order records are retained in anonymised form for eight years because Indian tax law requires it.
          Birth details, consultation notes and consent records are purged immediately.
        </p>
      </Container>
    </Section>
  );
}

export function Account() {
  useSEO({ title: "Your account", noindex: true });
  return (
    <Section>
      <Container className="max-w-2xl">
        <Eyebrow>Guest tracking, no login required</Eyebrow>
        <H1 className="mt-4">Your account</H1>
        <Lede className="mt-5">
          We do not force account creation to buy anything. Everything you need is reachable with the
          order number or booking reference from your confirmation email.
        </Lede>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {[
            ["/track", "Track an order", "Dispatch status, delivery date and tracking number."],
            ["/account/data", "Download or delete my data", "One-click export, one-click purge, emailed confirmation."],
            ["/legal/refunds", "Start a return", "Thirty days, prepaid label, money back to your card."],
            ["/contact", "Talk to a named person", "Acknowledged within 24 hours, resolved within 15 days."],
          ].map(([to, title, note]) => (
            <Link key={to} to={to} className="card-s p-6 transition-colors hover:border-gold">
              <H3>{title}</H3>
              <p className="ui mt-2 text-sm text-dim">{note}</p>
            </Link>
          ))}
        </div>
      </Container>
    </Section>
  );
}
